import { DollarOutlined } from '@ant-design/icons';
import { Button, Col, Form, Row, Space, Table, Tag } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { setCommonModal } from '../../../common/slices/modalSlice';
import { IAllInvoices } from '../../../common/types/commonInterfaces';
import { debounce, formatAmount, separateClient } from '../../../common/utils/common.utils';
import usePagination from '../../../common/utils/usePagination';
import useUrlParams from '../../../common/utils/useUrlParams';
import BreadCrumb from '../../../components/common/breadCrumb/BreadCrumb';
import { DateInput, FormInput } from '../../../components/common/FormItem/FormItems';
import Reload from '../../../components/common/reload/Reload';
import { loadingIndicator } from '../../../components/common/spinner/LoadingIndicator';
import { formatDateRangeArray, parseDateRangeObject } from '../../../helpers/healper';
import { useGetAirticketPartialReportsQuery } from '../../../modules/IATA_AIR_TICKET/api/endpoints/airticketInvoiceEndpoints';
import InvoiceOtherAddMoneyModal from '../../../modules/Invoice(Other)/Modals/InvoiceOtherAddMoneyModal';
import SetRemainder from './SetRmainder';

const AirticketpartialPayment = () => {
  const [invoiceInfo, setInvoiceInfo] = useState<IAllInvoices>();
  const [openModal, setOpenModal] = useState(false);
  const { setUrlParam, setUrlParamsFromObject, getAllUrlParams } = useUrlParams();
  const { page, size, changePagination, pagination } = usePagination();
  const params_value = getAllUrlParams() as {
    search: string | null;
    from_date: string | null;
    to_date: string | null;
  };

  const {
    data,
    isFetching,
    refetch: getAllRefetch,
    isLoading: dataIsLoading,
  } = useGetAirticketPartialReportsQuery({
    ...getAllUrlParams(),
    page,
    size,
  });
  const dispatch = useDispatch();
  const showModal = (record: IAllInvoices) => {
    dispatch(
      setCommonModal({
        title: 'Remainder',
        content: <SetRemainder invoiceData={record} />,
        show: true,
        width: 1024,
      })
    );
  };
  const columns: ColumnsType<IAllInvoices> = [
    {
      title: 'SL.',
      render: (_, data, index) => (
        <>{((pagination?.current || 1) - 1) * (pagination?.pageSize || 50) + 1 + index}</>
      ),
    },
    {
      title: 'Invoice No',
      render: (record) => (
        <Link target='_blank' to={`/airticket/details/${record?.invoice_id}`}>
          {record?.invoice_no || 'N/A'}
        </Link>
      ), // Add null check
    },
    {
      title: 'Type',
      dataIndex: 'invoice_reissue_client_type',
      key: 'invoice_reissue_client_type',
      render: (item) => {
        let color = 'red';
        if (item === 'IUR') {
          color = 'cyan';
        } else if (item === 'PNR') {
          color = 'purple';
        } else {
          color = 'pink';
        }
        return <Tag color={color}>{item || 'Not Available'}</Tag>;
      },
    },
    {
      title: 'Sales Date',
      key: 'invoice_date',
      dataIndex: 'invoice_date',
      render: (_, data) => {
        // Add null/undefined check for date
        if (!data?.invoice_date) return 'N/A';
        const date = moment(data.invoice_date);
        return date.isValid() ? date.format('DD MMM YYYY') : 'Invalid Date';
      },
      align: 'center',
    },
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      key: 'client_name',
      render: (value, record) => {
        // Add null checks
        if (!record?.comb_client) {
          return record?.client_name || 'N/A';
        }

        const { client_id, combined_id } = separateClient(record.comb_client);

        return (
          <Link
            to={
              client_id
                ? `/reports/client_ledger?client_query=${client_id}`
                : `/reports/combined_ledger?id=${combined_id}`
            }
          >
            <span>
              {record.client_name || 'N/A'} / {record.client_entry_id || 'N/A'}
            </span>
          </Link>
        );
      },
    },
    {
      title: 'Initial Payment Amount',
      dataIndex: 'initial_payment_amount',
      key: 'initial_payment_amount',
      align: 'right',
      render: (curr) => <span className='color-sales'>{formatAmount(curr || 0)}</span>,
    },
    {
      title: 'Sales Price',
      dataIndex: 'net_total',
      key: 'net_total',
      align: 'right',
      sorter: (a, b) => Number(a.net_total || 0) - Number(b.net_total || 0),
      render: (curr) => <span className='color-sales'>{formatAmount(curr || 0)}</span>,
    },

    {
      title: 'Rec Amount',
      dataIndex: 'invclientpayment_amount',
      key: 'invclientpayment_amount',
      align: 'right',
      render: (curr) => <span className='color-collection'>{formatAmount(curr || 0)}</span>,
    },
    {
      title: 'Due Amount',
      align: 'right',
      render(_, record) {
        const netTotal = Number(record?.net_total || 0);
        const paidAmount = Number(record?.invclientpayment_amount || 0);
        const dueAmount = netTotal - paidAmount;

        return dueAmount > 0 ? (
          <span style={{ color: 'red' }}>{formatAmount(dueAmount)}</span>
        ) : (
          <Tag color='cyan' icon={<DollarOutlined />}>
            PAID
          </Tag>
        );
      },
      sorter: (a, b) => {
        const aDue = Number(a.net_total || 0) - Number(a.invclientpayment_amount || 0);
        const bDue = Number(b.net_total || 0) - Number(b.invclientpayment_amount || 0);
        return aDue - bDue;
      },
    },
    {
      title: 'MR. No.',
      dataIndex: 'money_receipt_num',
      key: 'money_receipt_num',
      render: (_, data) => {
        // Add null check for money_receipt_num
        if (!data?.money_receipt_num) return 'N/A';

        const receipts = data.money_receipt_num.split(',');
        return (
          <span>
            {receipts.slice(0, 3).map((item, index) => (
              <li key={index}>{item.trim()}</li>
            ))}
            {receipts.length > 3 && <span>...</span>}
          </span>
        );
      },
    },
    {
      title: 'Due Date',
      dataIndex: 'partial_due_date',
      key: 'partial_due_date',
      render: (date) => {
        if (!date) return <span style={{ color: '#999' }}>N/A</span>;
        const today = dayjs();
        const targetDate = dayjs(date);
        const diff = targetDate.diff(today, 'day');
        const formattedDate = targetDate.format('DD-MM-YYYY');

        return (
          <div>
            <div>{formattedDate}</div>
            <div
              style={{ fontWeight: 'bold', fontSize: '14px', color: diff < 0 ? 'red' : 'green' }}
            >
              {diff < 0 ? `${Math.abs(diff)} days ago` : `${diff} days remaining`}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Sales by',
      dataIndex: 'sales_by',
      key: 'sales_by',
      render: (value) => value || 'N/A', // Add null check
    },
    {
      title: 'Action',
      key: 'operation',
      render: (_, data, record) => {
        return (
          <Space size='small'>
            <Button
              disabled={
                Number(data?.net_total || 0) <= Number(data?.invclientpayment_amount || 0) ||
                !!data?.invoice_is_refund
              }
              onClick={() => ModalOperation(data)}
              size='small'
              type='primary'
            >
              Add Money
            </Button>
            <Button onClick={() => showModal(data as any)} size='small' type='primary' danger>
              Sent Remainder
            </Button>
          </Space>
        );
      },
    },
  ];

  // Add safety check for data mapping
  const dataWithKey =
    data?.data?.map((item, index) => ({
      key: String(index),
      ...item,
    })) || [];

  const [form] = useForm();

  const handelSearch = useCallback(
    debounce((e: string) => {
      setUrlParam('search', e);
    }, 600),
    []
  );

  useEffect(() => {
    form.setFieldsValue({
      search: params_value.search || '',
      date_range: parseDateRangeObject({
        from_date: params_value.from_date,
        to_date: params_value.to_date,
      }),
    });
  }, [params_value]);
  const ModalOperation = (data: IAllInvoices) => {
    setInvoiceInfo(data);
    setOpenModal(!openModal);
  };

  return (
    <>
      <BreadCrumb arrOfOption={['Reports', 'Air Ticket Partial Payment']} />

      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 10 }} justify={'space-between'}>
        <Col span={12}>
          <Row gutter={10}>
            <Col xs={24} sm={12} md={8} lg={8} style={{ display: 'flex' }}>
              <Col>
                <Reload
                  refetch={() => {
                    getAllRefetch();
                  }}
                />
              </Col>
            </Col>
          </Row>
        </Col>

        <Col span={12}>
          <Form form={form}>
            <Row justify={'end'} gutter={[10, 10]}>
              <DateInput
                name={'date_range'}
                rangePicker
                size={8}
                noStyle
                onChange={(e: any) => setUrlParamsFromObject(formatDateRangeArray(e))}
              />
              <FormInput
                name={'search'}
                placeholder='Search here ...'
                onChange={(e: string) => handelSearch(e.trim())}
              />
            </Row>
          </Form>
        </Col>
      </Row>
      {invoiceInfo?.invoice_id && (
        <InvoiceOtherAddMoneyModal
          invoice_id={invoiceInfo?.invoice_id}
          InvocieModalTitle={'Invoice Air Ticket'}
          disabled={false}
          setOpenModal={setOpenModal}
          openModal={openModal}
        />
      )}
      <div>
        <span style={{ marginLeft: '5px', float: 'left', marginBottom: '1rem' }}>
          You have total <strong>{data?.count || data?.data?.length || 0}</strong> rows.
        </span>
        <Table
          size='small'
          bordered
          rowClassName={(record) => {
            const {
              invoice_is_reissued: reissue,
              invoice_is_refund: refund,
              invoice_is_void: void_inv,
            } = record || {};
            let className = '';

            if (void_inv) return (className = 'void_inv-row-style');
            if (reissue) return (className = 'reissued-row-style');
            if (refund) return (className = 'refund-row-style');

            return className;
          }}
          loading={{
            spinning: dataIsLoading || isFetching,
            indicator: loadingIndicator,
          }}
          columns={columns}
          scroll={{ x: 'max-content' }}
          dataSource={dataWithKey}
          pagination={changePagination(data?.count)}
        />
      </div>
    </>
  );
};

export default AirticketpartialPayment;
